/* Thimbray Atelier: progressive enhancement, no credentials or third-party libraries. */
(() => {
  'use strict';
  const configNode = document.getElementById('thimbray-config');
  if (!configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }
  const status = document.getElementById('StoreStatus');
  const announce = message => { if (status) status.textContent = message; };
  let lastCartOpener = null;
  let mutationBusy = false;
  const variantRequests = new WeakMap();
  const parseHTML = text => new DOMParser().parseFromString(text, 'text/html');
  const showError = (node, message) => {
    if (!node) { announce(message); return; }
    node.textContent = message; node.hidden = false;
  };
  const clearError = node => { if (node) { node.textContent = ''; node.hidden = true; } };
  const requestJSON = async (url, init) => {
    let response;
    try { response = await fetch(url, { credentials: 'same-origin', ...init }); }
    catch { throw new Error('We could not confirm the update. Check your bag before trying again.'); }
    let payload;
    try { payload = await response.json(); }
    catch { throw new Error('We could not confirm the update. Check your bag before trying again.'); }
    if (!response.ok || payload.status >= 400) {
      const description = typeof payload.description === 'string' ? payload.description :
        typeof payload.message === 'string' ? payload.message : 'This change could not be completed. Please review the quantity and try again.';
      throw new Error(description);
    }
    return payload;
  };
  function openCart(opener) {
    const dialog = document.getElementById('CartDrawer');
    if (!config.drawer || !dialog || typeof dialog.showModal !== 'function') {
      location.assign(config.cart); return;
    }
    if (opener) lastCartOpener = opener;
    if (!dialog.open) dialog.showModal();
    dialog.querySelector('[data-close-dialog]')?.focus();
  }
  function applyCartSections(sections) {
    const drawer = document.getElementById('CartDrawer');
    const drawerHTML = sections?.['cart-drawer'];
    const countHTML = sections?.['cart-count'];
    if (!drawer || !drawerHTML || !countHTML) return false;
    const nextDrawer = parseHTML(drawerHTML).getElementById('CartDrawer');
    const nextCount = parseHTML(countHTML).querySelector('[data-cart-count]');
    if (!nextDrawer || !nextCount) return false;
    drawer.innerHTML = nextDrawer.innerHTML;
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = nextCount.textContent; });
    return true;
  }
  function finishCartUpdate(payload, opener, focusClose) {
    if (!applyCartSections(payload.sections)) {
      // A mutation may have succeeded even if section rendering failed. Never retry an add.
      location.assign(config.cart); return;
    }
    if (focusClose) openCart(opener);
    announce('Your bag has been updated.');
    document.dispatchEvent(new CustomEvent('thimbray:cart:updated', { detail: { cart: payload } }));
  }
  async function addProduct(form, button) {
    if (mutationBusy || !form.reportValidity()) return;
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput?.value || variantInput.disabled) return;
    const error = form.querySelector('[data-form-error]'); clearError(error);
    const data = new FormData(form);
    data.append('sections', 'cart-drawer,cart-count');
    data.append('sections_url', location.pathname);
    mutationBusy = true;
    const originalDisabled = button?.disabled ?? false;
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
    try {
      const payload = await requestJSON(config.add, { method: 'POST', body: data, headers: { Accept: 'application/json' } });
      if (!config.drawer) { location.assign(config.cart); return; }
      finishCartUpdate(payload, button, true);
    } catch (errorCaught) {
      showError(error, errorCaught instanceof Error ? errorCaught.message : 'Please check your bag before trying again.');
    } finally {
      mutationBusy = false;
      if (button?.isConnected) { button.disabled = originalDisabled; button.removeAttribute('aria-busy'); }
    }
  }
  async function changeLine(key, quantity, focusKey) {
    if (mutationBusy || !key || !Number.isSafeInteger(quantity) || quantity < 0) return;
    mutationBusy = true;
    const drawer = document.getElementById('CartDrawer');
    drawer?.setAttribute('aria-busy', 'true'); clearError(drawer?.querySelector('[data-cart-error]'));
    try {
      const payload = await requestJSON(config.change, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer', 'cart-count'], sections_url: location.pathname })
      });
      finishCartUpdate(payload, null, false);
      // Avoid leaving keyboard focus on a removed/replaced form element.
      const currentDrawer = document.getElementById('CartDrawer');
      const lineForm = [...(currentDrawer?.querySelectorAll('[data-cart-line-form]') || [])]
        .find(form => form.querySelector('[name="id"]')?.value === focusKey);
      (lineForm?.querySelector('[name="quantity"]') || currentDrawer?.querySelector('[data-close-dialog]'))?.focus();
    } catch (errorCaught) {
      showError(document.getElementById('CartDrawer')?.querySelector('[data-cart-error]'),
        errorCaught instanceof Error ? errorCaught.message : 'Your bag could not be updated.');
    } finally { mutationBusy = false; document.getElementById('CartDrawer')?.removeAttribute('aria-busy'); }
  }
  async function updateVariant(select) {
    const root = select.closest('[data-product-root]');
    if (!root) return;
    const chosen = select.selectedOptions[0];
    const siblingURL = chosen?.dataset.productUrl;
    if (siblingURL && new URL(siblingURL, document.baseURI).pathname !== new URL(root.dataset.productUrl, document.baseURI).pathname) {
      location.assign(siblingURL); return;
    }
    variantRequests.get(root)?.abort();
    const controller = new AbortController(); variantRequests.set(root, controller);
    const url = new URL(root.dataset.productUrl, document.baseURI);
    const options = [...root.querySelectorAll('[data-option-select]')].map(el => el.selectedOptions[0]?.dataset.optionValueId);
    if (options.some(value => !value)) return;
    url.searchParams.set('option_values', options.join(','));
    const pageURL = url.href;
    url.searchParams.set('section_id', root.dataset.sectionId);
    const focusId = select.id;
    const quantity = root.querySelector('[name="quantity"]')?.value;
    const errorNode = root.querySelector('[data-form-error]'); clearError(errorNode);
    root.setAttribute('aria-busy', 'true');
    root.querySelectorAll('[name="add"]').forEach(button => { button.disabled = true; });
    root.querySelectorAll('.dynamic-checkout').forEach(el => { el.inert = true; });
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('The selected option could not be loaded.');
      const html = parseHTML(await response.text());
      const next = html.querySelector('[data-product-root]');
      if (!next) throw new Error('The selected option could not be loaded.');
      if (variantRequests.get(root) !== controller) return;
      const nextQuantity = next.querySelector('[name="quantity"]');
      if (nextQuantity && quantity) {
        const min = Number(nextQuantity.min) || 1;
        const step = Number(nextQuantity.step) || 1;
        const max = nextQuantity.max ? Number(nextQuantity.max) : Infinity;
        const requested = Number(quantity);
        if (Number.isSafeInteger(requested) && requested >= min && requested <= max && (requested - min) % step === 0) nextQuantity.value = quantity;
      }
      root.replaceWith(next);
      history.replaceState({}, '', pageURL);
      document.getElementById(focusId)?.focus({ preventScroll: true });
      window.Shopify?.PaymentButton?.init?.();
      announce('Product options and price updated.');
      document.dispatchEvent(new CustomEvent('thimbray:product:updated', { detail: { root: next } }));
    } catch (errorCaught) {
      if (errorCaught?.name === 'AbortError') return;
      // Keep purchase disabled so a stale variant cannot be ordered by accident.
      showError(errorNode, 'We could not load that option. Select it again or reload this product before adding it to your bag.');
    } finally { if (variantRequests.get(root) === controller) root.removeAttribute('aria-busy'); }
  }
  function showMedia(button) {
    const gallery = button.closest('.product-gallery');
    if (!gallery) return;
    gallery.querySelectorAll('[data-media-id]').forEach(item => {
      item.hidden = item.dataset.mediaId !== button.dataset.mediaTarget;
      if (item.hidden) item.querySelectorAll('video').forEach(video => video.pause());
    });
    gallery.querySelectorAll('[data-media-target]').forEach(el => el.setAttribute('aria-pressed', String(el === button)));
  }
  async function loadRecommendations(node) {
    if (node.dataset.loaded || !node.dataset.url) return;
    node.dataset.loaded = 'true';
    try {
      const response = await fetch(node.dataset.url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const html = parseHTML(await response.text());
      const content = html.querySelector('[data-recommendations]');
      if (content) node.innerHTML = content.innerHTML;
    } catch { /* Optional merchandising must not prevent buying a product. */ }
  }
  function initialize(root = document) {
    root.querySelectorAll('[data-recommendations]').forEach(node => {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); loadRecommendations(node); }
        }, { rootMargin: '350px' }); observer.observe(node);
      } else loadRecommendations(node);
    });
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('[data-cart-line-form]')) {
      event.preventDefault(); if (!form.reportValidity()) return;
      const key = form.querySelector('[name="id"]')?.value;
      changeLine(key, Number(form.querySelector('[name="quantity"]')?.value), key); return;
    }
    if (form.matches('.product-form, .quick-add-form')) {
      // Accelerated checkout is its own provider flow; only intercept the standard Add button.
      if (event.submitter && event.submitter.name !== 'add') return;
      event.preventDefault(); addProduct(form, event.submitter || form.querySelector('[name="add"]'));
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-option-select]')) updateVariant(event.target);
  });
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const cartLink = target.closest('[data-cart-open]');
    if (cartLink && config.drawer && !event.ctrlKey && !event.metaKey && !event.shiftKey && event.button === 0) {
      event.preventDefault(); openCart(cartLink); return;
    }
    const close = target.closest('[data-close-dialog]');
    if (close) { close.closest('dialog')?.close(); return; }
    const remove = target.closest('[data-cart-remove]');
    if (remove) { changeLine(remove.dataset.cartRemove, 0, null); return; }
    const media = target.closest('[data-media-target]');
    if (media) { showMedia(media); return; }
    document.querySelectorAll('.nav-drop[open], .mobile-nav[open]').forEach(details => {
      if (!details.contains(target)) details.open = false;
    });
    const dialog = document.getElementById('CartDrawer');
    if (target === dialog) {
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }
  });
  document.addEventListener('close', event => {
    if (event.target.id === 'CartDrawer' && lastCartOpener?.isConnected) lastCartOpener.focus({ preventScroll: true });
  }, true);
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    const activeDetails = document.activeElement?.closest('.nav-drop[open], .mobile-nav[open]');
    if (activeDetails) { activeDetails.open = false; activeDetails.querySelector('summary')?.focus(); }
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  initialize();
})();
